import { useState } from "react";
import { RotateCcw, TrashIcon } from "lucide-react";
import { useForm } from "react-hook-form";

import BreadCrumb from "../../../Components/BreadCrumb";
import TableListing from "../../../Components/TableListing";
import { ToastMessage } from "../../../Components/Utils";
import { Button } from "../../../Components/Buttons";
import { FormSwitch, FormInput } from "../../../Components/Form";
import { handleError } from "../../../stores/components/handleError";
import {
  useFetchRolesQuery,
  useFetchTrashedRolesQuery,
  useCreateRoleMutation,
  useUpdateRoleMutation,
  useDeleteRoleMutation,
  useDestroyRoleMutation,
  useRestoreRoleMutation,
  useBulkDeleteRolesMutation,
  useBulkDestroyRolesMutation,
  useBulkRestoreRolesMutation,
} from "../../../stores/api/roles.api";
import payloadHelper from "../../../stores/api/helpers/payload.helper";
import rolesHelper from "../../../stores/api/helpers/roles.helper";

const EMPTY_PAGINATION = {
  data: [],
  current_page: 1,
  total_pages: 1,
  total_items: 0,
  current_items: 0,
  limit: 10,
};

const HEADERS = [
  { key: "id", label: "#", sortable: false },
  { key: "name", label: "Name", sortable: true },
  {
    key: "status",
    label: "Status",
    type: "status",
  },
];

const RoleForm = ({ defaultName = "", defaultStatus = "Active", onSave, onCancel }) => {
  const { register, handleSubmit, watch, setValue } = useForm({
    defaultValues: { name: defaultName, status: defaultStatus },
  });
  const status = watch("status");

  return (
    <form
      onSubmit={handleSubmit(onSave)}
      onKeyDown={(e) => e.key === "Escape" && onCancel()}
      className="w-full grid grid-cols-1 sm:grid-cols-[1fr_auto_auto] gap-3 items-center"
    >
      <FormInput
        label="Role Name"
        name="name"
        value={watch("name")}
        onChange={(val) => setValue("name", val)}
        placeholder="e.g. Manager"
        autoFocus
      />

      <div className="flex flex-col gap-1 justify-center">

          <FormSwitch
            checked={status === "Active" || status === true ? true : false}
            onChange={(val) =>
              setValue("status", val, {
                shouldDirty: true,
                shouldValidate: true,
              })
            }
          />

          <input type="hidden" {...register("status")} />
        </div>

      <div className="flex flex-col gap-1">
        <label className="text-xs font-medium text-transparent select-none">·</label>
        <div className="flex gap-2">
          <button type="submit" className="px-4 py-1.5 text-xs font-semibold rounded-md bg-orange-500 text-white hover:bg-orange-600 transition">Save</button>
          <button type="button" onClick={onCancel} className="px-4 py-1.5 text-xs font-semibold rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-slate-700 dark:text-gray-200 dark:hover:bg-slate-600 transition">Cancel</button>
        </div>
      </div>
    </form>
  );
};

const RolesList = () => {
  const [trashed, setTrashed] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const queryParams = payloadHelper.forList({
    page,
    limit,
    search,
    sortBy: sortConfig?.key?.toLowerCase(),
    sortOrder: sortConfig.direction,
  });

  const activeQuery = useFetchRolesQuery(queryParams, { skip: trashed });
  const trashedQuery = useFetchTrashedRolesQuery(queryParams, { skip: !trashed });
  const { data: rolesData = EMPTY_PAGINATION, isFetching } = trashed ? trashedQuery : activeQuery;

  const [createRole] = useCreateRoleMutation();
  const [updateRole] = useUpdateRoleMutation();
  const [deleteRole] = useDeleteRoleMutation();
  const [destroyRole] = useDestroyRoleMutation();
  const [restoreRole] = useRestoreRoleMutation();
  const [bulkDeleteRoles] = useBulkDeleteRolesMutation();
  const [bulkDestroyRoles] = useBulkDestroyRolesMutation();
  const [bulkRestoreRoles] = useBulkRestoreRolesMutation();

  const resetPage = () => setPage(1);
  const handleCreateSave = async ({ name, status }) => {
    try {
      const payload = {
        name: name?.trim() ?? "",
        status: Boolean(status),
      };

      await createRole(
        rolesHelper.forCreate(payload)
      ).unwrap();

      ToastMessage({
        type: "success",
        title: "Created!",
        message: `Role "${payload.name}" created.`,
      });
    } catch (err) {
      handleError(err, "Failed to create role.");
    }
  };
  const handleEditSave = async ({ name, status }, row) => {
    try {
      const payload = {
        name: name?.trim() ?? "",
        status: Boolean(status),
      };

      await updateRole(
        rolesHelper.forUpdate(row.id, payload)
      ).unwrap();

      ToastMessage({
        type: "success",
        title: "Updated!",
        message: `Role "${payload.name}" updated.`,
      });
    } catch (err) {
      handleError(err, "Failed to update role.");
    }
  };

  const handleSingle = (row, type = "delete") => {
    const verb = type === "restore" ? "Restore" : type === "destroy" ? "Destroy" : "Delete";
    ToastMessage({
      type: "warning",
      title: `${verb} Role`,
      message: `${verb} "${row.name}"?`,
      toastType: "confirm",
      onYes: async () => {
        try {
          if (type === "restore") {
            await restoreRole(row.id).unwrap();
          } else if (type === "destroy") {
            await destroyRole(row.id).unwrap();
          } else {
            await deleteRole(row.id).unwrap();
          }
          ToastMessage({ type: "success", title: "Done!", message: `Role ${verb.toLowerCase()}d.` });
        } catch (err) {
          handleError(err, "Failed.");
        }
      },
    });
  };

  const handleBulk = (selectedItems, type = "delete") => {
    const ids = selectedItems;
    const verb = type === "restore" ? "Restore" : type === "destroy" ? "Destroy" : "Delete";
    ToastMessage({
      type: "warning",
      title: "Bulk Action",
      message: `${verb} ${ids.length} roles?`,
      toastType: "confirm",
      onYes: async () => {
        try {
          const body = payloadHelper.forBulk(ids);
          if (type === "restore") {
            await bulkRestoreRoles(body).unwrap();
          } else if (type === "destroy") {
            await bulkDestroyRoles(body).unwrap();
          } else {
            await bulkDeleteRoles(body).unwrap();
          }
          
          ToastMessage({ type: "success", title: "Done!", message: `${ids.length} roles ${verb.toLowerCase()}d.` });
        } catch (err) {
          handleError(err, "Failed bulk action.");
        }
      },
    });
  };

  const handleTrashedChange = (value) => {
    setTrashed(value);
    resetPage();
  };

  return (
    <>
      <BreadCrumb title="Roles" />
      <TableListing
        headers={HEADERS}
        paginationData={rolesData}
        loading={isFetching}
        handleBulk={handleBulk}
        search={search}
        setSearch={setSearch}
        statusModule="role"
        trashed={trashed}
        setTrashed={setTrashed}
        onTrashedChange={handleTrashedChange}
        onPageChange={setPage}
        onRowsPerPageChange={(value) => {
          setLimit(value);
          resetPage();
        }}
        onSearch={(value) => {
          setSearch(value);
          resetPage();
        }}
        onSortChange={(sort) => {
          setSortConfig(sort);
          resetPage();
        }}
        inlineEdit={
          !trashed
            ? (row, _onSave, onCancel) => (
              <RoleForm
                defaultName={row.name}
                defaultStatus={row.status ?? "Active"}
                onSave={(data) => {
                  handleEditSave(data, row);
                  onCancel();
                }}
                onCancel={onCancel}
              />
            )
            : null
        }
        inlineCreate={
          !trashed
            ? (_onSave, onCancel) => (
              <RoleForm
                onSave={(data) => {
                  handleCreateSave(data);
                  onCancel();
                }}
                onCancel={onCancel}
              />
            )
            : null
        }
        onInlineEditSave={() => { }}
        onInlineCreateSave={() => { }}
        actions={(row) =>
          trashed ? (
            <div className="flex gap-2">
              <Button
                onClick={() => handleSingle(row, "restore")}
                title="Restore"
                icon={RotateCcw}
                className="text-green-600 hover:bg-green-100 hover:text-green-700 dark:hover:bg-green-500/10"
              />
              <Button
                onClick={() => handleSingle(row, "destroy")}
                title="Destroy"
                icon={TrashIcon}
                className="text-red-600 hover:bg-red-100 hover:text-red-700 dark:hover:bg-red-500/10"
              />
            </div>
          ) : (
            <Button
              onClick={() => handleSingle(row, "delete")}
              title="Delete"
              icon={TrashIcon}
              className="text-red-600 hover:bg-red-100 hover:text-red-700 dark:hover:bg-red-500/10"
            />
          )
        }
      />
    </>
  );
};

export default RolesList;
